import React from "react";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Wrench, Shield, Zap, Star, Database, Brain, FlaskConical } from "lucide-react";

const CHANGELOG = [
  {
    version: "1.4.0",
    date: "2026-04-09",
    title: { bg: "CMS система и модулен рефакторинг", en: "CMS System & Modular Refactoring" },
    entries: [
      { icon: Wrench, text: { bg: "CMS за Landing Page — редактирайте текстовете през Admin панела", en: "Landing Page CMS — edit texts via Admin Panel" } },
      { icon: Wrench, text: { bg: "Пълни имена на макросите в хранителния дневник (Протеин, Въглехидрати, Мазнини)", en: "Full macronutrient names in the meal log (Protein, Carbs, Fat)" } },
      { icon: Database, text: { bg: "Съхранение на лабораторните анализи с PDF документ", en: "Lab analysis persistence with PDF storage" } },
      { icon: Wrench, text: { bg: "Навигационни линкове 'За нас/Контакти/Цени' преместени само във footer", en: "Nav links 'About/Contact/Pricing' moved to footer only" } },
      { icon: Zap, text: { bg: "Рефакторинг на backend — 12 модулни route файла вместо 1 монолитен", en: "Backend refactoring — 12 modular route files instead of 1 monolith" } },
    ]
  },
  {
    version: "1.3.0",
    date: "2026-04-08",
    title: { bg: "Гейминификация и нови страници", en: "Gamification & New Pages" },
    entries: [
      { icon: Star, text: { bg: "Система за награди — дневно влизане, логване на хранене, AI запитвания", en: "Rewards system — daily login, meal logging, AI queries" } },
      { icon: Shield, text: { bg: "Правна страница (GDPR) и страница с ценоразпис", en: "Legal (GDPR) and Pricing pages" } },
      { icon: Wrench, text: { bg: "Преименуване на 'Кредити' → 'Точки' в цялата платформа", en: "Renamed 'Credits' → 'Points' across the entire platform" } },
      { icon: Wrench, text: { bg: "Подобрена видимост на Tooltips в тъмен режим", en: "Improved tooltip visibility in dark mode" } },
      { icon: Wrench, text: { bg: "Мобилно хамбургер меню на landing страницата", en: "Mobile hamburger menu on landing page" } },
    ]
  },
  {
    version: "1.2.0",
    date: "2026-04-07",
    title: { bg: "USDA интеграция и Admin подобрения", en: "USDA Integration & Admin Improvements" },
    entries: [
      { icon: Database, text: { bg: "Food Database Manager в Admin — USDA batch auto-fill за DIL изчисление", en: "Food Database Manager in Admin — USDA batch auto-fill for DIL calculation" } },
      { icon: Sparkles, text: { bg: "Управление на точки на потребители от Admin панела", en: "Admin point management for users" } },
      { icon: Wrench, text: { bg: "Страница 'За нас' и 'Контакти' с форма", en: "About and Contact pages with form" } },
      { icon: Brain, text: { bg: "Пълен BG превод на Onboarding Wizard", en: "Full BG translation of Onboarding Wizard" } },
      { icon: Wrench, text: { bg: "Подобрени Tooltips с обяснения за съкращенията", en: "Improved tooltips with abbreviation explanations" } },
    ]
  },
  {
    version: "1.1.0",
    date: "2026-04-06",
    title: { bg: "AI Meal Planner и Landing redesign", en: "AI Meal Planner & Landing Redesign" },
    entries: [
      { icon: Brain, text: { bg: "AI Meal Planner — 7-дневен персонализиран план от GPT-5.2", en: "AI Meal Planner — 7-day personalized plan from GPT-5.2" } },
      { icon: Sparkles, text: { bg: "Нов дизайн на Landing страницата (Info Page)", en: "New Landing page redesign (Info Page)" } },
      { icon: Wrench, text: { bg: "100 безплатни точки при регистрация", en: "100 free points on registration" } },
      { icon: Brain, text: { bg: "Подобрени AI отговори — емпатично форматиране с емоджита", en: "Improved AI responses — empathetic formatting with emojis" } },
    ]
  },
  {
    version: "1.0.0",
    date: "2026-04-01",
    title: { bg: "Първа версия на Nutramilo", en: "Nutramilo v1.0 Launch" },
    entries: [
      { icon: Brain, text: { bg: "Два AI асистента — EndoBot (ендокринолог) и NutriBot (диетолог)", en: "Two AI assistants — EndoBot (endocrinologist) & NutriBot (dietitian)" } },
      { icon: FlaskConical, text: { bg: "Валидиран метаболитен модел: DIL, DII, фенотипове P1–P5", en: "Validated metabolic model: DIL, DII, phenotypes P1–P5" } },
      { icon: Sparkles, text: { bg: "Здравни индекси (BMI, HOMA-IR, WHR, WHtR, Body Fat%)", en: "Health indices (BMI, HOMA-IR, WHR, WHtR, Body Fat%)" } },
      { icon: Shield, text: { bg: "JWT автентикация с brute-force защита", en: "JWT auth with brute-force protection" } },
      { icon: Database, text: { bg: "Хранителен дневник с 108+ валидирани храни", en: "Meal log with 108+ validated foods" } },
      { icon: Wrench, text: { bg: "Двуезичен интерфейс (BG/EN) и дневен/нощен режим", en: "Bilingual interface (BG/EN) and day/night theme" } },
    ]
  },
];

export default function ChangelogPage() {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[hsl(223,47%,7%)]' : 'bg-[#FAFBFE]'}`} data-testid="changelog-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/info" className={`inline-flex items-center gap-1.5 text-sm mb-8 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>
          <ArrowLeft className="w-4 h-4" /> {bg ? "Обратно" : "Back"}
        </Link>

        <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
          {bg ? "Списък с промени" : "Changelog"}
        </h1>
        <p className={`text-sm mb-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {bg ? "Всички обновления и подобрения на платформата" : "All platform updates and improvements"}
        </p>

        <div className="relative">
          {/* Timeline line */}
          <div className={`absolute left-[11px] top-2 bottom-2 w-0.5 ${isDark ? 'bg-[hsl(223,47%,18%)]' : 'bg-gray-200'}`} />

          <div className="space-y-10">
            {CHANGELOG.map((release, ri) => (
              <div key={`release-${release.version}`} className="relative pl-10">
                {/* Dot */}
                <div className={`absolute left-0 top-1.5 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  ri === 0
                    ? 'bg-[#6366F1] border-[#6366F1]'
                    : isDark ? 'bg-[hsl(223,47%,12%)] border-[hsl(223,47%,22%)]' : 'bg-white border-gray-300'
                }`}>
                  {ri === 0 && <Sparkles className="w-3 h-3 text-white" />}
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-xs font-mono px-2 py-0.5 rounded ${isDark ? 'bg-[hsl(223,47%,15%)] text-[#818CF8]' : 'bg-indigo-50 text-[#6366F1]'}`}>
                    v{release.version}
                  </span>
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {new Date(release.date).toLocaleDateString(bg ? "bg-BG" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
                  </span>
                </div>

                <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
                  {bg ? release.title.bg : release.title.en}
                </h3>

                <div className="space-y-2">
                  {release.entries.map((entry, ei) => {
                    const Icon = entry.icon;
                    return (
                      <div key={`${release.version}-${ei}`} className={`flex items-start gap-2.5 p-2.5 rounded-lg ${isDark ? 'bg-[hsl(223,47%,10%)]' : 'bg-white border border-gray-100'}`}>
                        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-[#818CF8]' : 'text-[#6366F1]'}`} />
                        <span className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {bg ? entry.text.bg : entry.text.en}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
